class SmarttBattConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.state = {
      goal: 'zelf',
      phase: '3-fase',
      inverter: '15',
      capacity: 32,
      backup: true,
      installation: true
    };

    this.inverters = {
      '1-fase': ['3', '5', '6.5', '8'],
      '3-fase': ['9', '15', '19.5', '24']
    };

    this.systemPrices = {
      '1-fase|5|3': 2995,
      '1-fase|5|5': 3495,
      '1-fase|16|3': 5495,
      '1-fase|16|5': 5695,
      '1-fase|16|8': 6695,
      '1-fase|32|5': 8164.13,
      '1-fase|32|8': 8978.88,
      '3-fase|5|9': 5495,
      '3-fase|16|9': 7895,
      '3-fase|16|15': 8695,
      '3-fase|32|9': 11295,
      '3-fase|32|15': 11995,
      '3-fase|32|19.5': 12295,
      '3-fase|32|24': 14295,
      '3-fase|48|15': 15295,
      '3-fase|48|19.5': 15495,
      '3-fase|48|24': 17995
    };

    this.variantAvailability = {};
    const installation1Phase = Number(this.dataset.installation1Phase);
    const installation3Phase = Number(this.dataset.installation3Phase);
    this.installationPrices = {
      '1-fase': Number.isFinite(installation1Phase) ? installation1Phase : 1815,
      '3-fase': Number.isFinite(installation3Phase) ? installation3Phase : 3025
    };

    const variantData = this.querySelector('[data-shopify-variants]');
    if (variantData) {
      try {
        const parsed = JSON.parse(variantData.textContent);
        const livePrices = {};
        const liveAvailability = {};
        parsed.variants.forEach((variant) => {
          const phase = String(variant.option1).toLowerCase().startsWith('1') ? '1-fase' : String(variant.option1).toLowerCase().startsWith('3') ? '3-fase' : null;
          const capacity = Number.parseFloat(String(variant.option2).replace(',', '.'));
          const inverter = Number.parseFloat(String(variant.option3).replace(',', '.'));
          if (!phase || !Number.isFinite(capacity) || !Number.isFinite(inverter) || !Number.isFinite(variant.price_cents)) return;
          const key = `${phase}|${capacity}|${inverter}`;
          livePrices[key] = variant.price_cents / 100;
          liveAvailability[key] = Boolean(variant.available);
        });
        if (Object.keys(livePrices).length) {
          this.systemPrices = livePrices;
          this.variantAvailability = liveAvailability;
        }
      } catch (error) {
        console.warn('SmarttBatt configurator: Shopify-variantdata kon niet worden gelezen.', error);
      }
    }

    this.presets = {
      besparen: { phase: '1-fase', inverter: '5', capacity: 5, backup: false },
      backup: { phase: '3-fase', inverter: '15', capacity: 16, backup: true },
      peak: { phase: '3-fase', inverter: '15', capacity: 32, backup: false },
      offgrid: { phase: '3-fase', inverter: '24', capacity: 48, backup: true }
    };

    const initialAvailable = this.getAvailableInverters(this.state.phase, this.state.capacity);
    if (!initialAvailable.includes(this.state.inverter) && initialAvailable.length) this.state.inverter = initialAvailable[0];

    this.querySelectorAll('[data-goal]').forEach((button) => {
      button.addEventListener('click', () => this.applyGoal(button.dataset.goal));
    });

    this.querySelectorAll('[data-phase]').forEach((button) => {
      button.addEventListener('click', () => {
        this.state.goal = 'zelf';
        const phase = button.dataset.phase;
        const capacity = phase === '1-fase' && this.state.capacity >= 48 ? 32 : this.state.capacity;
        const available = this.getAvailableInverters(phase, capacity);
        const preferred = phase === '1-fase' ? '5' : '15';
        this.state.phase = phase;
        this.state.capacity = capacity;
        this.state.inverter = available.includes(preferred) ? preferred : available[0];
        this.render();
      });
    });

    this.querySelectorAll('[data-inverter]').forEach((button) => {
      button.addEventListener('click', () => {
        this.state.goal = 'zelf';
        this.state.inverter = button.dataset.inverter;
        this.render();
      });
    });

    this.querySelectorAll('[data-capacity]').forEach((button) => {
      button.addEventListener('click', () => {
        this.state.goal = 'zelf';
        this.chooseCapacity(Number(button.dataset.capacity));
        this.render();
      });
    });

    this.querySelector('[data-capacity-down]')?.addEventListener('click', () => {
      this.state.goal = 'zelf';
      this.chooseCapacity(this.state.capacity <= 16 ? 5 : this.state.capacity - 16);
      this.render();
    });

    this.querySelector('[data-capacity-up]')?.addEventListener('click', () => {
      this.state.goal = 'zelf';
      this.chooseCapacity(this.state.capacity === 5 ? 16 : this.state.capacity + 16);
      this.render();
    });

    const backup = this.querySelector('[data-backup]');
    const installation = this.querySelector('[data-installation]');
    backup?.addEventListener('change', () => {
      this.state.backup = backup.checked;
      this.render();
    });
    installation?.addEventListener('change', () => {
      this.state.installation = installation.checked;
      this.render();
    });

    this.querySelectorAll('[data-address-field]').forEach((field) => {
      field.addEventListener('input', () => this.syncForm());
    });
    this.querySelectorAll('[data-contact-preference]').forEach((field) => {
      field.addEventListener('change', () => this.syncContactPreference());
    });
    this.querySelector('form')?.addEventListener('submit', () => this.syncForm());

    this.syncContactPreference();
    this.render();

    const successMessage = this.querySelector('[data-form-success]');
    if (successMessage) {
      window.setTimeout(() => {
        successMessage.focus({ preventScroll: true });
        successMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 120);
    }
  }

  applyGoal(goal) {
    this.state.goal = goal;
    const preset = this.presets[goal];
    if (preset) Object.assign(this.state, preset);
    const available = this.getAvailableInverters(this.state.phase, this.state.capacity);
    if (!available.includes(this.state.inverter)) this.state.inverter = available[0];
    this.render();
    this.querySelector('#builder')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  setText(selector, value) {
    this.querySelectorAll(selector).forEach((element) => { element.textContent = value; });
  }

  getSystemPrice(phase, capacity, inverter) {
    return this.systemPrices[`${phase}|${capacity}|${inverter}`] ?? null;
  }

  getAvailableInverters(phase, capacity) {
    if (capacity > 48) return this.inverters[phase];
    return this.inverters[phase].filter((inverter) => {
      const key = `${phase}|${capacity}|${inverter}`;
      return this.getSystemPrice(phase, capacity, inverter) !== null && this.variantAvailability[key] !== false;
    });
  }

  chooseCapacity(capacity) {
    const phase = capacity >= 48 && this.state.phase === '1-fase' ? '3-fase' : this.state.phase;
    const available = this.getAvailableInverters(phase, capacity);
    const preferred = phase === '1-fase' ? '5' : '15';
    this.state.phase = phase;
    this.state.capacity = capacity;
    this.state.inverter = available.includes(this.state.inverter) ? this.state.inverter : available.includes(preferred) ? preferred : available[0];
  }

  formatPrice(value) {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  render() {
    const { phase, inverter, capacity, backup, installation, goal } = this.state;
    const inverterCount = phase === '3-fase' ? 3 : 1;
    const systemPrice = this.getSystemPrice(phase, capacity, inverter);
    const installationPrice = this.installationPrices[phase];
    const totalPrice = systemPrice === null ? null : systemPrice + (installation ? installationPrice : 0);
    const systemPriceLabel = systemPrice === null ? 'Prijs op aanvraag' : this.formatPrice(systemPrice);
    const installationPriceLabel = installation ? this.formatPrice(installationPrice) : 'Niet gekozen';
    const totalPriceLabel = totalPrice === null ? 'Prijs op aanvraag' : this.formatPrice(totalPrice);
    const batteryUnits = capacity === 5 ? 1 : capacity / 16;
    const batteryLabel = capacity === 5 ? '1 compact 5 kWh-blok' : `${batteryUnits} × 16 kWh RUIXU`;

    this.querySelectorAll('[data-goal]').forEach((button) => button.classList.toggle('active', button.dataset.goal === goal));
    this.querySelectorAll('[data-phase]').forEach((button) => button.classList.toggle('active', button.dataset.phase === phase));
    this.querySelectorAll('[data-inverter-group]').forEach((group) => { group.hidden = group.dataset.inverterGroup !== phase; });
    this.querySelectorAll('[data-inverter]').forEach((button) => {
      const available = capacity > 48 || this.getAvailableInverters(phase, capacity).includes(button.dataset.inverter);
      button.disabled = !available;
      button.classList.toggle('active', button.dataset.inverter === inverter);
      const label = button.querySelector('span');
      if (label) label.textContent = available ? button.dataset.detail : 'Niet beschikbaar';
    });
    this.querySelectorAll('[data-capacity]').forEach((button) => button.classList.toggle('active', Number(button.dataset.capacity) === capacity));

    const capacityScale = this.querySelector('[data-capacity-scale]');
    capacityScale?.classList.toggle('active', capacity > 48);
    this.setText('[data-capacity-value]', `${capacity} kWh`);
    this.setText('[data-capacity-label]', batteryLabel);
    const down = this.querySelector('[data-capacity-down]');
    if (down) down.disabled = capacity === 5;

    const backupInput = this.querySelector('[data-backup]');
    const installationInput = this.querySelector('[data-installation]');
    if (backupInput) backupInput.checked = backup;
    if (installationInput) installationInput.checked = installation;

    const inverterVisual = this.querySelector('[data-inverter-visual]');
    if (inverterVisual) inverterVisual.replaceChildren(...Array.from({ length: inverterCount }, () => document.createElement('span')));
    const batteryVisual = this.querySelector('[data-battery-visual]');
    if (batteryVisual) {
      batteryVisual.classList.toggle('compact', capacity === 5);
      batteryVisual.replaceChildren(...Array.from({ length: Math.min(batteryUnits, 5) }, () => document.createElement('span')));
      if (batteryUnits > 5) {
        const extra = document.createElement('b');
        extra.textContent = `+${batteryUnits - 5}`;
        batteryVisual.append(extra);
      }
    }

    this.setText('[data-summary-phase]', phase);
    this.setText('[data-summary-inverter]', `${inverter} kVA`);
    this.setText('[data-summary-inverter-count]', String(inverterCount));
    this.setText('[data-summary-capacity]', `${capacity} kWh`);
    this.setText('[data-summary-backup]', backup ? 'Inbegrepen' : 'Niet gekozen');
    this.setText('[data-summary-installation]', installation ? 'Gecertificeerd door SmarttBatt' : 'Zelf regelen');
    this.setText('[data-system-price]', systemPriceLabel);
    this.setText('[data-installation-price]', installationPriceLabel);
    this.setText('[data-total-price]', totalPriceLabel);
    this.setText('[data-mobile-total]', totalPriceLabel);
    this.setText('[data-summary-battery-count]', String(batteryUnits));
    this.setText('[data-summary-battery-label]', capacity === 5 ? 'compact RUIXU-blok' : 'grote RUIXU-batterij(en)');
    this.setText('[data-summary-backup-short]', backup ? 'Ja' : 'Nee');
    this.setText('[data-component-inverter]', `${inverter} kVA totaal`);
    this.setText('[data-component-capacity]', `${capacity} kWh gekozen`);
    this.setText('[data-component-installation]', installation ? 'Gecertificeerd door SmarttBatt' : 'Niet gekozen');
    this.setText('[data-package-title]', `${phase} · ${inverter} kVA · ${capacity} kWh`);
    this.setText('[data-package-subtitle]', `${backup ? 'Met back-up' : 'Zonder back-up'} · ${installation ? 'Gecertificeerde installatie door SmarttBatt' : 'Installatie zelf regelen'}`);
    this.setText('[data-package-inverter]', `Omvormervermogen: ${inverter} kVA`);
    this.setText('[data-package-capacity]', `RUIXU-opslag: ${capacity} kWh`);
    this.setText('[data-package-phase]', `Aansluiting: ${phase}`);
    this.setText('[data-package-installation]', `Realisatie: ${installation ? 'SmarttBatt inclusief opleverdocumentatie' : 'zelf regelen'}`);
    this.setText('[data-package-system-price]', `Systeemprijs: ${systemPriceLabel}`);
    this.setText('[data-package-installation-price]', `Installatie: ${installationPriceLabel}`);
    this.setText('[data-package-total]', `Totaal vanaf: ${totalPriceLabel}`);

    this.syncForm();
  }

  syncForm() {
    const postcode = this.querySelector('[data-postcode]')?.value.trim() || '';
    const houseNumber = this.querySelector('[data-house-number]')?.value.trim() || '';
    const address = [postcode, houseNumber, 'Nederland'].filter(Boolean).join(' ');
    const mapsUrl = postcode && houseNumber ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : '';
    const { phase, inverter, capacity, backup, installation } = this.state;
    const systemPrice = this.getSystemPrice(phase, capacity, inverter);
    const installationPrice = this.installationPrices[phase];
    const totalPrice = systemPrice === null ? null : systemPrice + (installation ? installationPrice : 0);
    const configuration = [
      `Aansluiting: ${phase}`,
      `Omvormervermogen: ${inverter} kVA`,
      `RUIXU-opslag: ${capacity} kWh`,
      `Back-up: ${backup ? 'ja' : 'nee'}`,
      `Installatie door SmarttBatt: ${installation ? 'ja' : 'nee'}`,
      `Systeemprijs incl. btw: ${systemPrice === null ? 'prijs op aanvraag' : this.formatPrice(systemPrice)}`,
      `Installatieprijs incl. btw: ${installation ? this.formatPrice(installationPrice) : 'niet gekozen'}`,
      `Totaal vanaf incl. btw: ${totalPrice === null ? 'prijs op aanvraag' : this.formatPrice(totalPrice)}`
    ].join('\n');

    const configInput = this.querySelector('[data-form-configuration]');
    const mapsInput = this.querySelector('[data-form-maps]');
    if (configInput) configInput.value = configuration;
    if (mapsInput) mapsInput.value = mapsUrl;
    const mapsLink = this.querySelector('[data-map-link]');
    if (mapsLink) {
      mapsLink.href = mapsUrl || '#';
      mapsLink.hidden = !mapsUrl;
    }
  }

  syncContactPreference() {
    const selected = this.querySelector('[data-contact-preference]:checked')?.value || '';
    const phoneRequired = selected === 'Telefonisch bespreken';
    const phoneInput = this.querySelector('[data-phone-field]');
    if (phoneInput) phoneInput.required = phoneRequired;
    const requiredMarker = this.querySelector('[data-phone-required-marker]');
    const optionalMarker = this.querySelector('[data-phone-optional]');
    if (requiredMarker) requiredMarker.hidden = !phoneRequired;
    if (optionalMarker) optionalMarker.hidden = phoneRequired;
  }
}

if (!customElements.get('smarttbatt-configurator')) {
  customElements.define('smarttbatt-configurator', SmarttBattConfigurator);
}

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('smarttbatt-configurator').forEach((element) => element.connectedCallback());
});
